import {
  APPROVAL_REQUEST_AUDIT_ACTIONS,
  approvalRequestNotifications,
  type ApprovalRequestAuditPayload,
} from "@legal-500/discovery/contracts/approval";
import {
  TESTIMONIAL_AUDIT_ACTIONS,
  testimonialNotifications,
  type TestimonialAuditPayload,
} from "@legal-500/discovery/contracts/content-pipeline";
import { auditService, type AuditEvent, type AuditEventFilters } from "./audit.service";
import {
  researchAuditService,
  type ResearchAuditLogFilters,
  type ResearchAuditLogItem,
} from "./researchAudit.service";

// ============================================================================
// Types
// ============================================================================

export type ChangeAuditSource = "approval-requests" | "testimonials" | "research-cms";

/** Matches every automated writer: the actor filter sends it as `actorType=system`. */
export const SYSTEM_ACTOR = "system:";

export const COMMENTARY_SNAPSHOT = "commentary_snapshot";
export const COMMENTARY_SNAPSHOT_ERROR = "commentary_snapshot_error";

export type ChangeAuditEntry = {
  id: string;
  entityId: string;
  action: string;
  /** Frontegg user id, or the system writer's name when no person made the change. */
  actor: string;
  actorEmail: string | null;
  actorType: "user" | "system" | "anonymous" | null;
  occurredAt: string;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  metadata: Record<string, unknown> | null;
  firmName: string | null;
  publicationName: string | null;
  lawyerName: string | null;
  externalId: string | null;
  entityType?: string;
  changedFields?: Record<string, unknown>;
};

export type ChangeAuditPage = {
  items: ChangeAuditEntry[];
  nextCursor: string | null;
};

export type ChangeAuditFilters = {
  entityId?: string;
  /** `SYSTEM_ACTOR` for automated changes, an email, or a user id. */
  actor?: string;
  action?: string;
  /** ISO lower bound (inclusive). */
  from?: string;
  /** ISO upper bound (inclusive). */
  to?: string;
  /** Substring of the firm name. */
  firm?: string;
  table?: string;
  cursor?: string;
  limit?: number;
};

// ============================================================================
// Service
// ============================================================================

type EventBackedSource = Exclude<ChangeAuditSource, "research-cms">;

const EVENT_TYPE_PREFIX: Record<EventBackedSource, string> = {
  "approval-requests": `${approvalRequestNotifications.service}:${approvalRequestNotifications.stream}`,
  testimonials: `${testimonialNotifications.service}:${testimonialNotifications.stream}`,
};

export const CHANGE_AUDIT_ACTIONS: Record<ChangeAuditSource, readonly string[]> = {
  "approval-requests": APPROVAL_REQUEST_AUDIT_ACTIONS,
  testimonials: TESTIMONIAL_AUDIT_ACTIONS,
  "research-cms": ["INSERT", "UPDATE", "DELETE"],
};

type ChangeAuditPayload = Partial<ApprovalRequestAuditPayload & TestimonialAuditPayload>;

export function toChangeAuditEntry(source: EventBackedSource, event: AuditEvent): ChangeAuditEntry {
  const payload = (event.payload ?? {}) as ChangeAuditPayload;
  const prefix = EVENT_TYPE_PREFIX[source];
  return {
    id: event.id,
    entityId: event.subject ?? payload.approvalRequestId ?? payload.testimonialId ?? "",
    action: event.type.slice(prefix.length + 1),
    actor: event.actor?.id ?? "unknown",
    actorEmail: event.actor?.email ?? null,
    actorType: event.actor?.type ?? null,
    occurredAt: event.occurredAt,
    changes: payload.changes ?? null,
    metadata: payload.metadata ?? null,
    firmName: payload.firmName ?? null,
    publicationName: payload.publicationName ?? null,
    lawyerName: payload.lawyerName ?? null,
    externalId: payload.externalId ?? null,
  };
}

type ActorFilter = { actorId?: string; actorEmail?: string; actorType?: "system" };

function actorFilter(actor: string | undefined): ActorFilter {
  if (!actor) return {};
  if (actor === SYSTEM_ACTOR) return { actorType: "system" };
  if (actor.includes("@")) return { actorEmail: actor };
  return { actorId: actor };
}

function toEventFilters(source: EventBackedSource, filters: ChangeAuditFilters): AuditEventFilters {
  const prefix = EVENT_TYPE_PREFIX[source];
  return {
    type: filters.action ? `${prefix}:${filters.action}` : `${prefix}:*`,
    subject: filters.entityId,
    ...actorFilter(filters.actor),
    firmName: filters.firm,
    from: filters.from,
    to: filters.to,
    cursor: filters.cursor,
    limit: filters.limit,
  };
}

function toResearchFilters(filters: ChangeAuditFilters): ResearchAuditLogFilters {
  return {
    ...actorFilter(filters.actor),
    action: filters.action,
    entityType: filters.table,
    entityId: filters.entityId,
    from: filters.from,
    to: filters.to,
    limit: filters.limit,
    cursor: filters.cursor,
  };
}

function updatedFields(item: ResearchAuditLogItem): ChangeAuditEntry["changes"] {
  if (item.action !== "UPDATE" || !item.changedFields) return null;
  const changes: NonNullable<ChangeAuditEntry["changes"]> = {};
  for (const [field, change] of Object.entries(item.changedFields)) {
    if (field === COMMENTARY_SNAPSHOT || field === COMMENTARY_SNAPSHOT_ERROR) continue;
    const { from, to } = (change ?? {}) as { from?: unknown; to?: unknown };
    changes[field] = { from, to };
  }
  return changes;
}

function toResearchAuditEntry(item: ResearchAuditLogItem): ChangeAuditEntry {
  return {
    id: item.id,
    entityId: item.entityId,
    action: item.action,
    actor: item.actorId,
    actorEmail: item.actorEmail,
    actorType: item.actorType,
    occurredAt: item.createdAt,
    changes: updatedFields(item),
    metadata: null,
    firmName: null,
    publicationName: null,
    lawyerName: null,
    externalId: null,
    entityType: item.entityType,
    changedFields: item.changedFields ?? {},
  };
}

export const changeAudit = {
  list: async (
    source: ChangeAuditSource,
    filters: ChangeAuditFilters = {},
  ): Promise<ChangeAuditPage> => {
    if (source === "research-cms") {
      const { auditLog, pagination } = await researchAuditService.list(toResearchFilters(filters));
      return { items: auditLog.map(toResearchAuditEntry), nextCursor: pagination.nextCursor };
    }
    const { events, nextCursor } = await auditService.list(toEventFilters(source, filters));
    return { items: events.map(event => toChangeAuditEntry(source, event)), nextCursor };
  },
};
